/**
 * Modelo de usuário autenticado
 * Baseado no user_model.dart do Flutter
 */
import { AUTHORIZATION_GROUP, MAINTENANCE_GROUP_IDS, normalizeGroupId, THIRD_PARTY_GROUP_IDS } from '../../../core/config/authorization-groups';
import { AUTHORIZATION_ROLE } from '../../../core/config/authorization-roles';
import { environment } from '../../../../environments/environment';
export interface UserModel {
  id: string;
  displayName?: string;
  givenName?: string;
  surname?: string;
  mail?: string;
  userPrincipalName?: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
  mobilePhone?: string | null;
  businessPhones?: string[];
  onPremisesSamAccountName?: string;
  onPremisesExtensionAttributes?: Record<string, string | null>;
  gerencia?: string;
  diretoria?: string;
  nucleo?: string;
  name: string;
  email: string;
  username: string;
  roles: string[];
  groups?: string[];
  permissions: string[];
  tenantId: string;
  lastLogin: Date;
  isActive: boolean;
  samAccountName?: string;
  extensionAttributes?: Record<string, string | null>;
  profile?: UserProfile;
}

/**
 * Perfil detalhado do usuário
 */
export interface UserProfile {
  firstName: string;
  lastName: string;
  displayName: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
  phoneNumber?: string;
  avatar?: string;
  preferredLanguage?: string;
  timeZone?: string;
  gerencia?: string;
  diretoria?: string;
  nucleo?: string;
}

/**
 * Claims do usuário do token JWT
 */
export interface UserClaims {
  sub: string;
  name: string;
  email: string;
  roles: string[];
  preferred_username: string;
  tenant_id: string;
  given_name?: string;
  family_name?: string;
  job_title?: string;
  department?: string;
  groups?: string[];
}

/**
 * Perfis de usuário do sistema PREVI
 */
export enum UserRole {
  PARTICIPANTE = 'participante',
  FUNCIONARIO = 'funcionario',
  GESTOR = 'gestor',
  ADMIN = 'admin'
}

/**
 * Permissões específicas do sistema
 */
export enum UserPermission {
  // Propostas
  VIEW_PROPOSTAS = 'view_propostas',
  CREATE_PROPOSTAS = 'create_propostas',
  EDIT_PROPOSTAS = 'edit_propostas',
  APPROVE_PROPOSTAS = 'approve_propostas',
  REJECT_PROPOSTAS = 'reject_propostas',
  
  // Documentos
  VIEW_DOCUMENTOS = 'view_documentos',
  UPLOAD_DOCUMENTOS = 'upload_documentos',
  VALIDATE_DOCUMENTOS = 'validate_documentos',
  
  // Contratos
  VIEW_CONTRATOS = 'view_contratos',
  GENERATE_CONTRATOS = 'generate_contratos',
  SIGN_CONTRATOS = 'sign_contratos',
  
  // Relatórios
  VIEW_RELATORIOS = 'view_relatorios',
  EXPORT_RELATORIOS = 'export_relatorios',
  
  // Administração
  MANAGE_USERS = 'manage_users',
  MANAGE_SYSTEM = 'manage_system'
}

/**
 * Helper para criar usuário básico
 */
export function createBasicUser(accountInfo: any): UserModel {
  return {
    id: accountInfo.localAccountId || accountInfo.homeAccountId,
    name: accountInfo.name || accountInfo.username,
    email: accountInfo.username,
    username: accountInfo.username,
    roles: extractRoles(accountInfo),
    groups: extractGroups(accountInfo),
    permissions: extractPermissions(accountInfo),
    tenantId: accountInfo.tenantId || '',
    lastLogin: new Date(),
    isActive: true,
    profile: {
      firstName: accountInfo.name?.split(' ')[0] || '',
      lastName: accountInfo.name?.split(' ').slice(1).join(' ') || '',
      displayName: accountInfo.name || accountInfo.username
    }
  };
}

/**
 * Extrai permissões baseadas nas roles
 */
function extractPermissions(accountInfo: any): string[] {
  const roles = extractRoles(accountInfo);
  const permissions: string[] = [];
  
  if (roles.includes(UserRole.ADMIN)) {
    permissions.push(
      UserPermission.MANAGE_USERS,
      UserPermission.MANAGE_SYSTEM,
      UserPermission.VIEW_RELATORIOS,
      UserPermission.EXPORT_RELATORIOS
    );
  }
  
  if (roles.includes(UserRole.GESTOR)) {
    permissions.push(
      UserPermission.APPROVE_PROPOSTAS,
      UserPermission.REJECT_PROPOSTAS,
      UserPermission.VIEW_RELATORIOS
    );
  }
  
  if (roles.includes(UserRole.FUNCIONARIO)) {
    permissions.push(
      UserPermission.VIEW_PROPOSTAS,
      UserPermission.CREATE_PROPOSTAS,
      UserPermission.EDIT_PROPOSTAS,
      UserPermission.VIEW_DOCUMENTOS,
      UserPermission.UPLOAD_DOCUMENTOS
    );
  }
  
  return permissions;
}

/**
 * Extrai roles do token/claims
 */
function extractRoles(accountInfo: any): string[] {
  const groups = extractGroups(accountInfo);
  const normalizedGroups = new Set(groups.map(normalizeGroupId));
  const mappedRoles: string[] = [];

  const hasGesopAccess = normalizedGroups.has(normalizeGroupId(AUTHORIZATION_GROUP.GESOP));
  const hasMaintenanceAccess = !environment.production && MAINTENANCE_GROUP_IDS
    .some(groupId => normalizedGroups.has(normalizeGroupId(groupId)));
  const hasThirdPartyMembership = THIRD_PARTY_GROUP_IDS
    .some(groupId => normalizedGroups.has(normalizeGroupId(groupId)));
  const hasThirdPartyAccess = !environment.production && hasThirdPartyMembership;

  if (hasGesopAccess || hasMaintenanceAccess || hasThirdPartyAccess) {
    mappedRoles.push(AUTHORIZATION_ROLE.BACKOFFICE_ACCESS);
  }

  // A associação ao grupo de terceirizados tem precedência para esta capacidade.
  // Assim, uma conta que também pertença a outro grupo autorizado não recebe
  // parametrização enquanto permanecer vinculada ao grupo FFI.
  if (!hasThirdPartyMembership && (hasGesopAccess || hasMaintenanceAccess)) {
    mappedRoles.push(AUTHORIZATION_ROLE.PARAMETERIZATION_ACCESS);
  }

  return mappedRoles;
}

function extractGroups(accountInfo: any): string[] {
  const groups = accountInfo?.idTokenClaims?.groups;
  return Array.isArray(groups) ? groups.filter((group): group is string => typeof group === 'string') : [];
}
